/**
 * =============================================================================
 * WlTransferOwnerDialog
 *
 * Modal do Painel WL para transferir o proprietário (owner) de um tenant.
 * - Mostra o proprietário atual e lista apenas membros ATIVOS e ADMINISTRADORES
 *   do tenant como candidatos (o servidor revalida tudo).
 * - Motivo obrigatório (10–500 caracteres, sem HTML).
 * - Executa a RPC `wl_transfer_tenant_owner` (permissão + papel owner_wl/admin_wl
 *   validados no banco, auditoria em wl_audit_log).
 *
 * @version 1.0.1
 * RELEASE: 2026-10-04 v1.0.1 - Candidatos passam a considerar o papel 'tenant_admin'.
 * RELEASE: 2026-10-04 v1.0.0 - Versão inicial.
 * =============================================================================
 */
import { useEffect, useMemo, useState } from "react";
import { Crown, Loader2, Search } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { describeWlError } from "@/hooks/wl/useWlErrorToast";
import { useWlTenantMembers } from "@/hooks/wl/useWlTenantDetails";
import { cn } from "@/lib/utils";

interface Props {
  tenantId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** Remove tags HTML e limita o tamanho do motivo (o banco revalida). */
/** Papéis que identificam administrador do tenant (atual + legado). */
const ADMIN_ROLES = ["tenant_admin", "admin"];

const sanitizeReason = (v: string) => v.replace(/<[^>]*>/g, "").slice(0, 500);

export function WlTransferOwnerDialog({ tenantId, open, onOpenChange }: Props) {
  const { toast } = useToast();
  const qc = useQueryClient();
  const { data: members = [], isLoading } = useWlTenantMembers(open ? tenantId : null);

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<string | null>(null);
  const [reason, setReason] = useState("");

  // Reseta o formulário a cada abertura
  useEffect(() => {
    if (open) { setSearch(""); setSelected(null); setReason(""); }
  }, [open]);

  const currentOwner = members.find((m) => m.is_owner);

  // Candidatos: ativos, administradores e diferentes do proprietário atual
  const candidates = useMemo(() => {
    const q = search.trim().toLowerCase();
    return members.filter((m) =>
      // v1.0.1: administrador do tenant é 'tenant_admin' (padrão atual); 'admin' mantido como legado
      !m.is_owner && m.is_active && (m.roles ?? []).some((r) => ADMIN_ROLES.includes(r.role)) &&
      (!q || (m.full_name ?? "").toLowerCase().includes(q) || (m.email_masked ?? "").toLowerCase().includes(q)));
  }, [members, search]);

  const transfer = useMutation({
    mutationFn: async () => {
      // RPC fora da tipagem gerada: preservar o `this` do cliente
      const rpc = supabase.rpc.bind(supabase) as unknown as (fn: string, args: Record<string, unknown>) => Promise<{ error: unknown }>;
      const { error } = await rpc("wl_transfer_tenant_owner", {
        p_tenant_id: tenantId,
        p_new_owner_user_id: selected,
        p_reason: reason.trim(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast({ title: "Proprietário transferido", description: "O novo proprietário já está ativo no tenant." });
      qc.invalidateQueries({ queryKey: ["wl_tenant_members", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl_tenant_overview", tenantId] });
      qc.invalidateQueries({ queryKey: ["wl-tenants-list"] });
      onOpenChange(false);
    },
    onError: (err) => toast({ title: "Falha ao transferir", description: describeWlError(err), variant: "destructive" }),
  });

  const reasonOk = reason.trim().length >= 10;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-4 w-4 text-amber-600" />
            Transferir proprietário
          </DialogTitle>
          <DialogDescription className="text-xs">
            O proprietário atual continua como membro e administrador; apenas o selo de proprietário é transferido.
          </DialogDescription>
        </DialogHeader>

        {/* Proprietário atual */}
        <div className="space-y-1">
          <Label className="text-xs">Proprietário atual</Label>
          <div className="rounded-md border p-3 text-sm">
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : currentOwner ? (
              <>
                <div className="font-medium">{currentOwner.full_name ?? "—"}</div>
                <div className="text-xs text-muted-foreground">
                  {currentOwner.email_masked ?? "—"}
                  {currentOwner.joined_at && ` · desde ${format(new Date(currentOwner.joined_at), "dd/MM/yyyy", { locale: ptBR })}`}
                </div>
              </>
            ) : <span className="text-muted-foreground">Nenhum proprietário definido</span>}
          </div>
        </div>

        {/* Novo proprietário */}
        <div className="space-y-1">
          <Label className="text-xs">Novo proprietário (somente administradores ativos)</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value.slice(0, 100))}
              placeholder="Buscar por nome ou e-mail" className="h-8 pl-7 text-sm" />
          </div>
          <div className="max-h-56 overflow-y-auto rounded-md border divide-y">
            {!isLoading && candidates.length === 0 && (
              <p className="p-3 text-xs text-muted-foreground">
                {search.trim() ? "Nenhum administrador encontrado para a busca." : "Nenhum administrador ativo disponível."}
              </p>
            )}
            {candidates.map((m) => (
              <button key={m.user_id} type="button" onClick={() => setSelected(m.user_id)}
                className={cn("w-full px-3 py-2 text-left text-sm hover:bg-muted/50",
                  selected === m.user_id && "bg-primary/10")}>
                <div className="font-medium">{m.full_name ?? "—"}</div>
                <div className="text-xs text-muted-foreground">{m.email_masked ?? "—"}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Motivo */}
        <div className="space-y-1">
          <Label htmlFor="transfer-reason" className="text-xs">Motivo (obrigatório)</Label>
          <Textarea id="transfer-reason" rows={3} value={reason}
            onChange={(e) => setReason(sanitizeReason(e.target.value))}
            placeholder="Ex.: proprietário anterior saiu da operação" className="text-sm" />
          <p className="text-right text-[10px] text-muted-foreground">{reason.length}/500</p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={!selected || !reasonOk || transfer.isPending} onClick={() => transfer.mutate()}>
            {transfer.isPending && <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />}
            Confirmar transferência
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
